<script setup lang="ts">
/**
 * THE source picker for the document browser (AlexAmin/marcus#158).
 *
 * ── ONE COMPONENT, BOTH SURFACES ───────────────────────────────────────────────────────────────
 *
 * The Documentos sidebar rail and the full-page finder at `/app/docs` both mount this. They had
 * already drifted apart once — two hand-built segmented controls listing the same modes in
 * different orders with different labels — and a source filter that behaves differently depending
 * on where it appears is the defect this ticket exists to remove. `size` is the ONLY difference
 * either surface is allowed: the rail is a dense `xs` toolbar, the finder an `sm` one.
 *
 * ── WHY A DROPDOWN AND NOT A SEGMENTED CONTROL ─────────────────────────────────────────────────
 *
 * The segmented control cost one cell per source, so at the rail's width "Conversaciones" was
 * clipped mid-word — and it was single-select, which cannot say "sheets AND conversations". The
 * collapsed trigger here costs one line whatever the selection is, which is what fixes the clipping
 * rather than working around it.
 *
 * State lives in the documents store (`activeSources`), not here: both surfaces filter the same
 * browser, so a selection made in one is the selection in the other.
 *
 * ── AND SINCE AlexAmin/marcus#169 IT IS THE GROUPING CHIP ──────────────────────────────────────
 *
 * The finder's toolbar collapsed into the column headers, and grouping did NOT go with it. That is
 * the decision Draft 2 was chosen on: grouping RESTRUCTURES the list rather than narrowing it, so it
 * must never be found by accident behind a header caret. It stays visible, and it says what it does
 * — the `sm` trigger reads `Agrupar: <selección>` instead of leaving the reader to infer that a
 * source list is a grouping.
 *
 * The prefix is keyed on `size`, which stays the ONLY difference between the two surfaces: the rail
 * is 288px wide and resizes to 200, where a five-character prefix on every label is exactly the kind
 * of thing that clips mid-word — the defect #158 existed to fix. No `variant` prop, no container
 * query, no second component.
 *
 * The empty-set item is relabelled rather than added. It has always been here ("Todas las fuentes",
 * calling `setSources([])`), and that label became actively wrong once the trigger said `Agrupar:`:
 * nothing is being grouped in that state, and it is not "all sources" either. It is the folder tree
 * — which is what BOTH surfaces render for it, the rail included (its breadcrumb and flat listing
 * are `isFolderMode`'s branch). Same state, same single definition (`activeSources.size === 0`),
 * honest name.
 */
import { Check, ChevronDown, Briefcase, Building2, Table2, MessagesSquare, FolderTree, Rows3 } from 'lucide-vue-next'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { storeToRefs } from 'pinia'
import { useDocumentsStore, DOCUMENT_SOURCES, type DocumentSource } from '~/stores/documents'

const props = withDefaults(defineProps<{ size?: 'xs' | 'sm' }>(), { size: 'sm' })
/** Fired after the store has been updated, for surface-local resets (each toolbar's search box). */
const emit = defineEmits<{ change: [] }>()

const { t } = useI18n()
const documents = useDocumentsStore()
const casesStore = useCasesStore()
const clientsStore = useClientsStore()
const { activeSources, orderedSources } = storeToRefs(documents)

/**
 * Groups are DERIVED from the cases and clients stores (MAR-125), so selecting a source has to make
 * sure the entities it groups by are loaded. `PanelLayout` covers the mount case; this covers
 * selecting a source later. Both stores carry a `loaded` flag, so it is a no-op once populated —
 * being careless here would mean a refetch on every checkbox click.
 *
 * It lives in the picker rather than in each toolbar because it is a fact about SELECTING a source,
 * and both surfaces would otherwise carry their own copy of it — which is how the two drifted apart
 * in the first place.
 */
function commit(next: () => void) {
  next()
  if (activeSources.value.size > 0) {
    if (!casesStore.loaded) void casesStore.listCases()
    if (!clientsStore.loaded) void clientsStore.listClients()
  }
  emit('change')
}

const ICONS: Record<DocumentSource, unknown> = {
  project: Building2,
  case: Briefcase,
  sheet: Table2,
  conversation: MessagesSquare,
}

function labelFor(source: DocumentSource): string {
  return t(`browser.groupBy.${source}`)
}

const options = computed(() =>
  DOCUMENT_SOURCES.map(source => ({ source, label: labelFor(source), icon: ICONS[source] })))

/**
 * The collapsed label. An empty selection is "no source filter" — the flat listing — and reads as
 * "Todas las fuentes" rather than naming a mode, because nothing is being filtered out.
 */
const triggerLabel = computed(() => {
  const selected = orderedSources.value
  if (selected.length === 0) return t('browser.sources.foldersShort')
  return selected.map(labelFor).join(', ')
})

/** `Agrupar:` on the finder, nothing on the rail — see the docblock. */
const showGroupingPrefix = computed(() => props.size === 'sm')

// `h-7 text-xs` matches the rail's search input; `h-9 text-sm` the finder's. Both are the height of
// the row they sit in, so the toolbar never reflows when the label grows.
const triggerClass = computed(() => props.size === 'xs'
  ? 'h-7 px-2 text-xs gap-1'
  : 'h-9 px-3 text-sm gap-1.5')
const iconClass = computed(() => props.size === 'xs' ? 'h-3.5 w-3.5' : 'h-4 w-4')
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="flex min-w-0 max-w-full items-center rounded-md border border-input bg-background font-medium transition-colors hover:bg-accent-lighter focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        :class="triggerClass"
        :aria-label="t('browser.sources.label')"
      >
        <Rows3 class="shrink-0 text-muted-foreground" :class="iconClass" />
        <!-- `truncate` on the label and `shrink-0` on the chevron: a long selection ellipsizes
             instead of pushing the caret out of the rail, which is the clipping this replaces. -->
        <span v-if="showGroupingPrefix" class="shrink-0 text-muted-foreground">{{ t('browser.sources.groupPrefix') }}</span>
        <span class="truncate">{{ triggerLabel }}</span>
        <ChevronDown class="shrink-0 text-muted-foreground" :class="iconClass" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="min-w-[200px]" align="start">
        <DropdownMenuLabel>{{ t('browser.sources.label') }}</DropdownMenuLabel>
        <!-- Plain `DropdownMenuItem`s with `@select.prevent`, not `DropdownMenuCheckboxItem`: the
             menu must stay OPEN across several ticks so two sources can be picked in one visit,
             and their `menuitem` role is what the e2e suite (and a screen reader user) finds them by. -->
        <DropdownMenuItem
          v-for="opt in options"
          :key="opt.source"
          @select.prevent="commit(() => documents.toggleSource(opt.source))"
        >
          <Check
            class="h-3.5 w-3.5"
            :class="activeSources.has(opt.source) ? 'text-accent-ink' : 'text-transparent'"
          />
          <component :is="opt.icon" class="h-3.5 w-3.5 text-muted-foreground" />
          {{ opt.label }}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <!--
          BELOW THE RULE, AND NOT A FIFTH CHECKBOX. The folder tree is a different kind of object —
          every document occupies exactly one path — so "Carpetas + Hojas" must stay unrepresentable,
          which is what the store's header comment demands and what the empty set expresses. The rule
          is what says so visually.

          It is also the way back to no grouping: unchecking the last source reaches the same state,
          but a user with three selected should not have to click three times to leave.
        -->
        <DropdownMenuItem @select.prevent="commit(() => documents.setSources([]))">
          <Check
            class="h-3.5 w-3.5"
            :class="activeSources.size === 0 ? 'text-accent-ink' : 'text-transparent'"
          />
          <FolderTree class="h-3.5 w-3.5 text-muted-foreground" />
          {{ t('browser.sources.folderTree') }}
        </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
